// ADR-054 WeeklySchedule v1 pure model. Wire is rawVal; all UI platforms mirror
// these invariants so invalid/overlapping rules are rejected before op:set.

export type ScheduleKind = 'detection' | 'recording';
export type RecordingRuleMode = 'continuous' | 'event' | 'off';
export type DetectionType = 'motion' | 'person' | 'vehicle' | 'animal' | 'package' | 'sound' | 'tamper';

export interface WeeklyRule {
  days_mask: number;
  start_min: number;
  end_min: number;
  mode?: RecordingRuleMode;
  detection_types?: DetectionType[];
}

export interface WeeklySchedule {
  v: 1;
  timezone: string;
  enabled: boolean;
  rules: WeeklyRule[];
}

export const DETECTION_TYPES: readonly DetectionType[] = ['motion', 'person', 'vehicle', 'animal', 'package', 'sound', 'tamper'];
const RECORD_MODES = new Set<RecordingRuleMode>(['continuous', 'event', 'off']);

export function currentTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function emptyWeeklySchedule(timezone = currentTimezone()): WeeklySchedule {
  return { v: 1, timezone, enabled: false, rules: [] };
}

function validTimezone(value: string): boolean {
  if (!value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function normalizeDetectionTypes(raw: unknown): DetectionType[] | undefined {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) throw new Error('detection_types must be an array');
  const allowed = new Set<DetectionType>(DETECTION_TYPES);
  const values = [...new Set(raw.map(String))].filter((v): v is DetectionType => allowed.has(v as DetectionType)).sort();
  if (values.length !== raw.length && new Set(raw.map(String)).size !== values.length) throw new Error('invalid detection_type');
  return values.length > 0 ? values : undefined;
}

function parseRule(raw: unknown, kind: ScheduleKind): WeeklyRule {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('invalid rule');
  const value = raw as Record<string, unknown>;
  const daysMask = Number(value.days_mask);
  const start = Number(value.start_min);
  const end = Number(value.end_min);
  if (!Number.isInteger(daysMask) || daysMask < 1 || daysMask > 127) throw new Error('invalid days_mask');
  if (!Number.isInteger(start) || start < 0 || start > 1439) throw new Error('invalid start_min');
  if (!Number.isInteger(end) || end < 1 || end > 1440 || start >= end) throw new Error('invalid end_min');
  if (kind === 'detection') {
    if (value.mode !== undefined || value.detection_types !== undefined) throw new Error('detection rule contains recording fields');
    return { days_mask: daysMask, start_min: start, end_min: end };
  }
  const mode = value.mode;
  if (typeof mode !== 'string' || !RECORD_MODES.has(mode as RecordingRuleMode)) throw new Error('invalid record mode');
  const types = normalizeDetectionTypes(value.detection_types);
  if (mode !== 'event' && types !== undefined) throw new Error('detection_types only valid for event mode');
  return { days_mask: daysMask, start_min: start, end_min: end, mode: mode as RecordingRuleMode, ...(types ? { detection_types: types } : {}) };
}

function ruleKey(rule: WeeklyRule): string {
  return [rule.start_min, rule.end_min, rule.mode ?? '', ...(rule.detection_types ?? [])].join(':');
}

export function canonicalizeWeeklySchedule(schedule: WeeklySchedule, kind: ScheduleKind): WeeklySchedule {
  if (schedule.v !== 1 || typeof schedule.enabled !== 'boolean' || !validTimezone(schedule.timezone)) throw new Error('invalid schedule header');
  if (!Array.isArray(schedule.rules) || schedule.rules.length > 32) throw new Error('too many rules');
  const parsed = schedule.rules.map((rule) => parseRule(rule, kind));
  for (let day = 0; day < 7; day += 1) {
    const intervals = parsed.filter((rule) => (rule.days_mask & (1 << day)) !== 0).sort((a, b) => a.start_min - b.start_min || a.end_min - b.end_min);
    for (let i = 1; i < intervals.length; i += 1) {
      if (intervals[i].start_min < intervals[i - 1].end_min) throw new Error('overlapping rules');
    }
  }
  const merged = new Map<string, WeeklyRule>();
  for (const rule of parsed) {
    const key = ruleKey(rule);
    const previous = merged.get(key);
    if (previous) previous.days_mask |= rule.days_mask;
    else merged.set(key, { ...rule, detection_types: rule.detection_types ? [...rule.detection_types] : undefined });
  }
  const rules = [...merged.values()].sort((a, b) => a.start_min - b.start_min || a.end_min - b.end_min || a.days_mask - b.days_mask || ruleKey(a).localeCompare(ruleKey(b)));
  if (rules.length > 32) throw new Error('too many rules');
  return { v: 1, timezone: schedule.timezone, enabled: schedule.enabled, rules };
}

export function parseWeeklySchedule(raw: unknown, kind: ScheduleKind): WeeklySchedule | null {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const o = value as Record<string, unknown>;
    return canonicalizeWeeklySchedule({
      v: Number(o.v) as 1,
      timezone: String(o.timezone ?? ''),
      enabled: o.enabled as boolean,
      rules: o.rules as WeeklyRule[],
    }, kind);
  } catch {
    return null;
  }
}

export function rulesForDay(schedule: WeeklySchedule, day: number): WeeklyRule[] {
  const bit = 1 << day;
  return schedule.rules
    .filter((rule) => (rule.days_mask & bit) !== 0)
    .map((rule) => ({ ...rule, days_mask: bit, detection_types: rule.detection_types ? [...rule.detection_types] : undefined }))
    .sort((a, b) => a.start_min - b.start_min || a.end_min - b.end_min);
}

export function replaceDayRules(schedule: WeeklySchedule, day: number, replacement: WeeklyRule[], kind: ScheduleKind): WeeklySchedule {
  const bit = 1 << day;
  const preserved = schedule.rules
    .map((rule) => ({ ...rule, days_mask: rule.days_mask & ~bit }))
    .filter((rule) => rule.days_mask !== 0);
  const dayRules = replacement.map((rule) => ({ ...rule, days_mask: bit }));
  return canonicalizeWeeklySchedule({ ...schedule, rules: [...preserved, ...dayRules] }, kind);
}

export function copyDayRules(schedule: WeeklySchedule, sourceDay: number, targetDays: number[], kind: ScheduleKind): WeeklySchedule {
  const source = rulesForDay(schedule, sourceDay);
  return [...new Set(targetDays)].reduce((next, day) => replaceDayRules(next, day, source, kind), schedule);
}

export function minuteToTime(minute: number): string {
  const safe = Math.max(0, Math.min(1440, minute));
  const hour = safe === 1440 ? 24 : Math.floor(safe / 60);
  const min = safe === 1440 ? 0 : safe % 60;
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function timeToMinute(value: string, allowEndOfDay = false): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (minute > 59 || hour > 24 || (hour === 24 && (minute !== 0 || !allowEndOfDay))) return null;
  return hour * 60 + minute;
}
